import {Box, Select, Text, TextInput, Title} from '@mantine/core';
import {useForm, zodResolver} from '@mantine/form';
import {WhiteBgButtonThin} from '@packages/ui';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {useState} from 'react';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {isReleasedNewRegisterFlow} from '../../../config/unReleasePage';
import {
  BADREQUEST_VALIDATION_MSG,
  PostRemindPassSchema,
} from '../../../core/entities/remindpass/schema';
import {getBirthDayLists} from '../../../core/factories/remindpass/getBirthDayLists';
import {
  type PostRemindPassDataType,
  usePostRemindPass,
} from '../../../core/repositories/remindPassRepository';
import {useIsAppWebView} from '../../../shared/hooks/useIsAppWebView';
import {useMediaQuery} from '../../../shared/services/useMediaQuery';

export const RemindPassPage: React.FC = () => {
  const isWebView = useIsAppWebView();
  const router = useRouter();
  const isNewRegisterFlow = isReleasedNewRegisterFlow();

  const TITLE = 'パスワードお忘れの方 | 外資就活ドットコム';
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.asPath)}`;

  const {isPC} = useMediaQuery();
  const {yearList, monthList, dayList} = getBirthDayLists();

  const [errorMessage, setErrorMessage] = useState<string>('');

  const form = useForm({
    validate: zodResolver(PostRemindPassSchema),
    initialValues: {
      email: '',
      lastName: '',
      firstName: '',
      year: '',
      month: '',
      day: '',
    },
  });

  const saveRemindPass = usePostRemindPass({
    onSuccessFnc() {
      setErrorMessage('');
      sessionStorage.setItem('remindPassEmail', form.values.email);
      if (isWebView) {
        location.href = '/remindpass/sent';
      } else {
        void router.push('/remindpass/sent');
      }
    },
    onErrorFnc() {
      setErrorMessage(BADREQUEST_VALIDATION_MSG);
    },
  });

  const submitPost = (data: PostRemindPassDataType) => {
    saveRemindPass.mutate(
      data && {
        ...data,
      },
    );
  };

  return (
    <>
      <Head>
        <title key="title">{TITLE}</title>
        <meta key="og:title" property="og:title" content={TITLE} />
        <meta key="og:url" property="og:url" content={currentUrl} />
        <link rel="canonical" href={currentUrl} key="canonical" />
      </Head>

      <Title
        order={1}
        className="border-t-4 border-primary-1 bg-white p-16 text-lg text-primary-1 pc:px-32"
      >
        パスワードの再設定
      </Title>
      <Box className="mt-[10px] mb-[20px] p-16 bg-white pc:px-32">
        <Text className="text-base mb-[10px]">
          ご登録いただいたメールアドレス、氏名、生年月日をご記入ください。
        </Text>
        <Text className="mb-[20px] text-black-3" style={{fontSize: '13px'}}>
          {`「大学発行のメールアドレス」${
            isNewRegisterFlow ? '(認証済みに限ります)' : ''
          }でも「${
            isNewRegisterFlow
              ? 'ログイン時に入力するメールアドレス'
              : 'いつも使うメールアドレス'
          }」でも構いません。`}
        </Text>
        <form
          onSubmit={form.onSubmit((data) => {
            submitPost(data);
          })}
        >
          <Box className="mb-16">
            <Box className="flex">
              <Box>
                <TextInput
                  className="w-[233px]"
                  label="メールアドレス"
                  placeholder="sample@xxxx.jp"
                  withAsterisk={true}
                  size={isPC ? 'sm' : 'md'}
                  {...form.getInputProps('email')}
                />
              </Box>
            </Box>
          </Box>
          <Box className="mb-16">
            <Box className="flex items-start">
              <TextInput
                className="w-[233px] mr-[20px]"
                label="姓"
                placeholder="姓"
                withAsterisk={true}
                size={isPC ? 'sm' : 'md'}
                {...form.getInputProps('lastName')}
              />
              <TextInput
                className="w-[233px]"
                label="名"
                placeholder="名"
                withAsterisk={true}
                size={isPC ? 'sm' : 'md'}
                {...form.getInputProps('firstName')}
              />
            </Box>
          </Box>
          <Box className="mb-16">
            <label
              className="text-base mr-[10px]"
              htmlFor="RemindPasswordBirthdayYear"
            >
              生年月日
            </label>
            <span
              className="mantine-103svbs mantine-InputWrapper-required mantine-TextInput-required"
              aria-hidden="true"
            >
              *
            </span>
            <Box className="flex items-start">
              <Select
                placeholder="--"
                data={yearList}
                className="w-[120px]"
                size={isPC ? 'sm' : 'md'}
                {...form.getInputProps('year')}
              />
              <Text className="flex items-center h-[36px]">-</Text>
              <Select
                placeholder="--"
                data={monthList}
                className="w-[120px]"
                size={isPC ? 'sm' : 'md'}
                {...form.getInputProps('month')}
              />
              <Text className="flex items-center h-[36px]">-</Text>
              <Select
                placeholder="--"
                data={dayList}
                className="w-[120px]"
                size={isPC ? 'sm' : 'md'}
                {...form.getInputProps('day')}
              />
            </Box>
          </Box>
          <WhiteBgButtonThin
            label="送信"
            type="submit"
            isPrimary={false}
            isDisabled={saveRemindPass.isLoading}
            className="w-full"
          />
          {errorMessage && (
            <Text className="text-notification mt-[10px]">{errorMessage}</Text>
          )}
        </form>
      </Box>
    </>
  );
};
